import { Link } from 'react-router-dom';
import {
  Diamond,
  ArrowLeft,
  ShieldCheck,
  Database,
  Cog,
  Archive,
  Mail,
} from 'lucide-react';

const SECTIONS = [
  {
    id: 'collect',
    icon: Database,
    title: 'Personal Information We Collect',
    body: (
      <>
        <p>
          When you visit the Site, we automatically collect certain information about
          your device, including information about your web browser,{' '}
          <span className="font-semibold text-[#111]">IP address</span>, time zone, and
          some of the cookies that are installed on your device.
        </p>
        <p className="mt-4">
          Additionally, when you make a purchase or attempt to make a purchase through
          the Site, we collect certain information from you. Because we use a{' '}
          <span className="font-semibold text-[#111]">manual UPI payment system</span>,
          this information primarily includes your{' '}
          <span className="font-semibold text-[#111]">
            name, WhatsApp phone number, email address, and UPI Reference/UTR numbers
          </span>
          .
        </p>
        <p
          className="mt-5 px-5 py-4 rounded-xl font-body text-[14.5px] text-[#444] leading-relaxed"
          style={{
            background: 'linear-gradient(135deg, #FFF7CD 0%, rgba(253,195,161,0.18) 100%)',
            border: '1px solid rgba(253,195,161,0.45)',
          }}
        >
          <ShieldCheck className="inline-block w-4 h-4 -mt-0.5 mr-2 text-[#c88a5e]" />
          We do <span className="font-semibold text-[#111]">not</span> directly collect
          or store traditional credit card data.
        </p>
      </>
    ),
  },
  {
    id: 'use',
    icon: Cog,
    title: 'How Do We Use Your Info?',
    body: (
      <>
        <p>
          We use the Order Information that we collect generally to fulfill any orders
          placed through the Site (including verifying your UPI payment and delivering
          your digital files via{' '}
          <span className="font-semibold text-[#111]">WhatsApp or email</span>).
        </p>
        <p className="mt-4">Additionally, we use this Information to:</p>
        <ul className="mt-4 space-y-3">
          {[
            'Communicate with you about your purchase or support requests.',
            'Deliver requested free assets (like the Weekly Free Drop).',
            'Provide customer support.',
            'Screen our orders for potential risk or fraud.',
          ].map((line, i) => (
            <li key={i} className="flex gap-3">
              <span
                className="mt-2 shrink-0 w-1.5 h-1.5 rounded-full"
                style={{ background: '#FDC3A1' }}
              />
              <span className="font-body text-[15px] text-[#444] leading-relaxed">
                {line}
              </span>
            </li>
          ))}
        </ul>
      </>
    ),
  },
  {
    id: 'retention',
    icon: Archive,
    title: 'Data Retention',
    body: (
      <p>
        When you place an order through the Site or join our mailing list, we will
        maintain your Information for our records{' '}
        <span className="font-semibold text-[#111]">
          unless and until you ask us to delete this information
        </span>
        .
      </p>
    ),
  },
  {
    id: 'contact',
    icon: Mail,
    title: 'Contact Us',
    body: (
      <p>
        For more information about our privacy practices, if you have questions, or if
        you would like to request data deletion, please contact us by e-mail at{' '}
        <a
          href="mailto:designcarat9@gmail.com"
          data-testid="privacy-contact-email"
          className="font-mono font-semibold text-[#111] underline decoration-[#FDC3A1] decoration-2 underline-offset-4 hover:decoration-[#c88a5e] transition-colors"
        >
          designcarat9@gmail.com
        </a>
        .
      </p>
    ),
  },
];

export default function PrivacyPolicy() {
  return (
    <main
      data-testid="privacy-policy-page"
      className="min-h-screen"
      style={{ background: '#FFF7CD' }}
    >
      {/* ───────── Top bar ───────── */}
      <header
        className="sticky top-0 z-30"
        style={{
          background: 'rgba(255,247,205,0.85)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          borderBottom: '1px solid rgba(0,0,0,0.06)',
        }}
      >
        <div className="max-w-3xl mx-auto px-6 sm:px-8 h-14 flex items-center justify-between">
          <Link
            to="/"
            data-testid="privacy-back-home"
            className="flex items-center gap-2 font-body text-sm text-[#555] hover:text-[#111] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
          <div className="flex items-center gap-2">
            <Diamond className="w-4 h-4 text-[#FDC3A1]" />
            <span className="font-heading font-bold text-[#111] text-sm tracking-tight">
              DesignCarat
            </span>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 sm:px-8 py-16 sm:py-24">
        {/* ───────── Title block ───────── */}
        <span
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-body text-[10px] font-bold uppercase tracking-[0.22em]"
          style={{
            background: 'rgba(253,195,161,0.18)',
            border: '1px solid rgba(253,195,161,0.5)',
            color: '#c88a5e',
          }}
          data-testid="privacy-eyebrow"
        >
          <ShieldCheck className="w-3 h-3" />
          Last Updated: May 2026
        </span>

        <h1
          data-testid="privacy-heading"
          className="mt-6 font-heading font-black text-[#111] tracking-tight"
          style={{ fontSize: 'clamp(2.25rem, 5vw, 3.5rem)', lineHeight: 1.05 }}
        >
          Privacy Policy
        </h1>
        <p className="mt-5 font-body text-base sm:text-lg text-[#666] leading-relaxed max-w-xl">
          This Privacy Policy describes how your personal information is collected, used,
          and shared when you visit or make a purchase from{' '}
          <span className="font-semibold text-[#111]">Design Carat</span> (the
          &ldquo;Site&rdquo;).
        </p>

        {/* ───────── Decorative gold divider ───────── */}
        <div
          className="my-12 h-px"
          style={{
            background:
              'linear-gradient(90deg, transparent, rgba(253,195,161,0.7), transparent)',
          }}
        />

        {/* ───────── Sections ───────── */}
        <div className="space-y-12">
          {SECTIONS.map(({ id, icon: Icon, title, body }, idx) => (
            <section key={id} data-testid={`privacy-section-${id}`}>
              <div className="flex items-center gap-3 mb-5">
                <span
                  className="flex items-center justify-center w-9 h-9 rounded-full"
                  style={{
                    background: 'rgba(253,195,161,0.2)',
                    border: '1px solid rgba(253,195,161,0.5)',
                  }}
                >
                  <Icon className="w-4 h-4 text-[#c88a5e]" />
                </span>
                <span
                  className="font-mono text-[10px] font-bold uppercase tracking-[0.22em]"
                  style={{ color: '#c88a5e' }}
                >
                  Section {String(idx + 1).padStart(2, '0')}
                </span>
              </div>
              <h2
                className="font-heading font-bold text-[#111] tracking-tight mb-5"
                style={{ fontSize: 'clamp(1.5rem, 3.2vw, 2rem)' }}
              >
                {title}
              </h2>
              <div className="font-body text-[15px] sm:text-base text-[#444] leading-relaxed">
                {body}
              </div>
            </section>
          ))}
        </div>

        {/* ───────── Footer note ───────── */}
        <div className="mt-20 pt-8" style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}>
          <p className="font-body text-xs text-[#999]">
            Questions about this policy? Email{' '}
            <a
              href="mailto:designcarat9@gmail.com"
              className="text-[#c88a5e] font-semibold hover:underline"
              data-testid="privacy-footer-email"
            >
              designcarat9@gmail.com
            </a>{' '}
            and we&rsquo;ll get back within 24 hours.
          </p>
          <Link
            to="/"
            data-testid="privacy-footer-back"
            className="mt-4 inline-flex items-center gap-2 font-body text-sm text-[#555] hover:text-[#111] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to DesignCarat
          </Link>
        </div>
      </div>
    </main>
  );
}
